import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { CreateAdminUser, DirectoryUser, UpdateAdminUser } from '@dcc-erp/shared';

import { api, apiJson } from '../api';

export interface AdminUser {
  id: string;
  email: string;
  displayName: string;
  isActive: boolean;
  defaultCompanyId: string;
  allowedCompanyIds: string[];
  globalRoleCodes: string[];
}

const key = ['admin-users'] as const;

export function useAdminUsers() {
  return useQuery({ queryKey: key, queryFn: () => api<AdminUser[]>('/admin/users') });
}

export function useUpdateAdminUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAdminUser }) =>
      apiJson<UpdateAdminUser, AdminUser>('PATCH', `/admin/users/${id}`, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key });
      void qc.invalidateQueries({ queryKey: ['company-users'] });
    },
  });
}

export function useDirectorySearch(q: string) {
  return useQuery({
    queryKey: ['admin-directory', q],
    queryFn: () => api<DirectoryUser[]>(`/admin/directory/search?q=${encodeURIComponent(q)}`),
    enabled: q.trim().length >= 2,
  });
}

export function useCreateAdminUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAdminUser) =>
      apiJson<CreateAdminUser, AdminUser>('POST', '/admin/users', data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key });
      void qc.invalidateQueries({ queryKey: ['company-users'] });
    },
  });
}
